import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass, Bell, CaretDown, Buildings, SignOut, Check,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { useI18n } from '../contexts/I18nContext';

function OrgPicker() {
  const { orgs, current, setCurrentId } = useOrg();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!current) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="org-picker-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-stone_line bg-white hover:bg-bg-alt transition-colors text-left min-w-[200px]"
      >
        <div className="w-8 h-8 rounded-lg bg-sage-100 text-sage-600 grid place-items-center shrink-0">
          <Buildings size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold leading-none">
            Organization
          </div>
          <div className="text-sm text-ink font-medium truncate mt-0.5">{current.name}</div>
        </div>
        {orgs.length > 1 && <CaretDown size={14} className="text-ink-muted" />}
      </button>
      {open && orgs.length > 1 && (
        <div
          data-testid="org-picker-menu"
          className="absolute z-50 mt-2 left-0 right-0 min-w-[260px] bg-white border border-stone_line rounded-xl shadow-card overflow-hidden animate-fade-up"
        >
          {orgs.map((o) => (
            <button
              key={o.id}
              data-testid={`org-picker-option-${o.id}`}
              onClick={() => {
                if (o.id !== current.id) {
                  setCurrentId(o.id);
                  // Detail pages reference entities scoped to the previous org
                  // (a patient/room/device UUID belongs to one tenant). Bounce
                  // to the dashboard so we never land on a 404 after switching.
                  navigate('/');
                }
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-alt text-left transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-sage-100 text-sage-600 grid place-items-center shrink-0">
                <Buildings size={16} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ink font-medium truncate">{o.name}</div>
                <div className="text-xs text-ink-muted truncate">{o.type.replace('_', ' ')}</div>
              </div>
              {o.id === current.id && <Check size={16} className="text-sage-600" weight="bold" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileMenu() {
  const { user, logout } = useAuth();
  const { isSuperAdmin } = useOrg();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const initials = (user?.name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  // Show the user's Telegram avatar when they've connected it; falls back to
  // initials on a sage background otherwise. The `imgError` state lets us
  // gracefully recover when Telegram's CDN occasionally serves a 404 (their
  // pictures rotate over time).
  const [imgError, setImgError] = useState(false);
  const tgPhoto = user?.telegram_photo_url;
  const showPhoto = Boolean(tgPhoto) && !imgError;
  const onLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="profile-menu-button"
        className="inline-flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-bg-alt transition-colors"
      >
        {showPhoto ? (
          <img
            src={tgPhoto}
            alt={user?.telegram_first_name || user?.name || ''}
            onError={() => setImgError(true)}
            data-testid="profile-avatar-photo"
            className="w-9 h-9 rounded-full object-cover border border-stone_line"
          />
        ) : (
          <div
            data-testid="profile-avatar-initials"
            className="w-9 h-9 rounded-full bg-sage text-white grid place-items-center text-sm font-semibold"
          >
            {initials}
          </div>
        )}
        <CaretDown size={14} className="text-ink-muted hidden sm:block" />
      </button>
      {open && (
        <div
          data-testid="profile-menu"
          className="absolute z-50 mt-2 right-0 w-[260px] bg-white border border-stone_line rounded-xl shadow-card overflow-hidden animate-fade-up"
        >
          <div className="px-4 py-3 border-b border-stone_line flex items-center gap-3">
            {/* Same avatar logic as the trigger button — Telegram photo when
                available, initials fallback otherwise. Slightly larger here
                (48px) so the dropdown feels like a profile card. */}
            {showPhoto ? (
              <img
                src={tgPhoto}
                alt={user?.telegram_first_name || user?.name || ''}
                onError={() => setImgError(true)}
                data-testid="profile-menu-avatar-photo"
                className="w-12 h-12 rounded-full object-cover border border-stone_line shrink-0"
              />
            ) : (
              <div
                data-testid="profile-menu-avatar-initials"
                className="w-12 h-12 rounded-full bg-sage text-white grid place-items-center text-base font-semibold shrink-0"
              >
                {initials}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-ink font-medium truncate">{user?.name}</div>
              <div className="text-xs text-ink-muted truncate">{user?.email}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {isSuperAdmin && (
                  <span data-testid="profile-superadmin-badge" className="inline-flex text-[10px] uppercase tracking-[0.18em] text-terracotta-700 bg-terracotta-100 px-2 py-0.5 rounded-full font-semibold">
                    {t('role_super_admin')}
                  </span>
                )}
                <span className="inline-flex text-[10px] uppercase tracking-[0.18em] text-sage-600 bg-sage-100 px-2 py-0.5 rounded-full">
                  {t(`role_${user?.role}`) || user?.role}
                </span>
              </div>
            </div>
          </div>
          {/* Language switcher removed — caregivers manage their language
              from Settings, which has the full 8-language dropdown. */}
          <button
            onClick={onLogout}
            data-testid="profile-logout"
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-bg-alt text-sm text-ink-soft hover:text-terracotta transition-colors"
          >
            <SignOut size={16} />
            {t('nav_signout')}
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const { t } = useI18n();
  const navigate = useNavigate();
  // Placeholder count — wired to real open-alerts count in Layout.
  return (
    <button
      onClick={() => navigate('/alerts')}
      data-testid="notifications-bell"
      className="relative p-2 rounded-xl hover:bg-bg-alt transition-colors text-ink-soft"
      aria-label={t('notifications')}
    >
      <Bell size={20} weight="duotone" />
    </button>
  );
}

export default function TopBar({ onOpenMobileMenu }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/patients?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-stone_line">
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
        <button
          onClick={onOpenMobileMenu}
          data-testid="topbar-mobile-menu"
          className="lg:hidden p-2 -ml-2 rounded-lg text-ink-soft hover:bg-bg-alt"
          aria-label={t('menu')}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="hidden md:block">
          <OrgPicker />
        </div>

        <form onSubmit={submit} className="flex-1 max-w-xl">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('topbar_search_placeholder')}
              data-testid="topbar-search"
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-stone_line bg-bg-alt/60 focus:bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all text-sm"
            />
          </div>
        </form>

        <NotificationsBell />
        <ProfileMenu />
      </div>

      <div className="md:hidden px-4 pb-3">
        <OrgPicker />
      </div>
    </div>
  );
}
